/**
 * useAppLifecycle
 * ---------------
 * Handles the "app went to background / OS killed the tab" case gracefully:
 *   1. Persist the last visited route to sessionStorage on every navigation
 *      so a fresh boot (after the OS killed the tab) restores the user
 *      where they were — instead of dumping them on `/` or the login page.
 *   2. Broadcast a `trigo:app-resumed` custom event when the tab returns to
 *      visible after being hidden > 5 s. Consumers (WebSocket manager,
 *      polling hooks, notifications hook) can listen and re-hydrate their
 *      state without forcing a page reload.
 *   3. Optional subtle toast telling the user "Reconnexion en cours…".
 */
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";

const ROUTE_KEY = "trigo:last-route";
const HIDDEN_AT_KEY = "trigo:hidden-at";

// Pages we never restore to (auth screens or one-shot pages).
const SKIP_RESTORE = new Set(["/", "/login", "/register", "/forgot-password"]);

export function useAppLifecycle() {
  const location = useLocation();
  const navigate = useNavigate();
  const bootRestoredRef = useRef(false);

  // ---- Persist current route on every navigation ---------------------------
  useEffect(() => {
    if (!location) return;
    const url = location.pathname + (location.search || "") + (location.hash || "");
    try {
      if (!SKIP_RESTORE.has(location.pathname)) {
        sessionStorage.setItem(ROUTE_KEY, url);
      }
    } catch (e) { /* storage denied */ }
  }, [location]);

  // ---- Restore last route on FIRST boot only -------------------------------
  // Only apply when the user lands on the root path AND we have a saved
  // deeper route. Prevents the "app restarts" UX because now we boot back
  // straight into the same page + tab.
  useEffect(() => {
    if (bootRestoredRef.current) return;
    bootRestoredRef.current = true;
    try {
      const saved = sessionStorage.getItem(ROUTE_KEY);
      if (
        saved &&
        SKIP_RESTORE.has(location.pathname) &&
        !SKIP_RESTORE.has(saved.split("?")[0])
      ) {
        navigate(saved, { replace: true });
      }
    } catch (e) { /* noop */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---- Visibility change → resume detection --------------------------------
  useEffect(() => {
    let hiddenAt = 0;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenAt = Date.now();
        try { sessionStorage.setItem(HIDDEN_AT_KEY, String(hiddenAt)); } catch (e) { /* noop */ }
      } else if (document.visibilityState === "visible") {
        const now = Date.now();
        const savedHidden = Number(sessionStorage.getItem(HIDDEN_AT_KEY) || 0);
        const away = now - (hiddenAt || savedHidden || now);
        if (away > 5000) {
          // Announce so WebSocket managers / data hooks can silently refresh.
          window.dispatchEvent(new CustomEvent("trigo:app-resumed", { detail: { awayMs: away } }));
          if (away > 30000) {
            toast.success("Bon retour !", {
              description: "Reconnexion à TrigoTour en cours…",
              duration: 2500,
            });
          }
        }
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);
}
